import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Send, Sparkles, Loader2, Users, Circle, Link2, X, Download, Share2, DoorOpen, ArrowLeft, Image as ImageIcon, Eye, EyeOff } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { MemberAvatar } from "@/components/MemberAvatar";
import { CoconBadge } from "@/components/CoconBadge";
import { CocoonMark } from "@/components/CocoonMark";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const L = {
  fr: { loading: "Le Cercle s'ouvre…", notfound: "Ce Cercle est introuvable ou dissous.", back: "Retour à l'accueil",
    around: "Dans le Cercle", joinTitle: "Rejoindre le Cercle", joinSub: "Choisis le prénom qui te représentera.", namePh: "Ton prénom", joinBtn: "Rejoindre", joining: "…", nameReq: "Un prénom, s'il te plaît.", photoNote: "Si tu es connecté·e, ta photo de profil sera visible par les autres participants.", hidePhoto: "Masquer ma photo ici",
    empty: "Le silence précède la première étincelle…", thinking: "L'Élan tisse le lien…", placeholder: "Partage ta pensée… (tape @élan pour l'appeler)",
    share: "Partager", close: "Clore le Cercle", closing: "…", sealTitle: "Le Sceau du Cercle", sealSaved: "Gardé dans le Carnet de chacun.", copy: "Copier le lien", copied: "Copié !", download: "Télécharger", dissolved: "Ce Cercle a été clos." },
  en: { loading: "The Circle is opening…", notfound: "This Circle is gone or dissolved.", back: "Back home",
    around: "In the Circle", joinTitle: "Join the Circle", joinSub: "Pick the name that will represent you.", namePh: "Your first name", joinBtn: "Join", joining: "…", nameReq: "A name, please.", photoNote: "If you're signed in, your profile photo will be visible to other participants.", hidePhoto: "Hide my photo here",
    empty: "Silence before the first spark…", thinking: "L'Élan is weaving the bond…", placeholder: "Share your thought… (type @élan to call it)",
    share: "Share", close: "Close the Circle", closing: "…", sealTitle: "The Seal of the Circle", sealSaved: "Kept in everyone's Notebook.", copy: "Copy link", copied: "Copied!", download: "Download", dissolved: "This Circle has been closed." },
  es: { loading: "El Círculo se abre…", notfound: "Este Círculo no existe o fue disuelto.", back: "Volver al inicio",
    around: "En el Círculo", joinTitle: "Unirse al Círculo", joinSub: "Elige el nombre que te representará.", namePh: "Tu nombre", joinBtn: "Unirse", joining: "…", nameReq: "Un nombre, por favor.", photoNote: "Si has iniciado sesión, tu foto de perfil será visible para los demás participantes.", hidePhoto: "Ocultar mi foto aquí",
    empty: "El silencio antes de la primera chispa…", thinking: "L'Élan teje el vínculo…", placeholder: "Comparte tu pensamiento… (escribe @élan para llamarlo)",
    share: "Compartir", close: "Cerrar el Círculo", closing: "…", sealTitle: "El Sello del Círculo", sealSaved: "Guardado en el Cuaderno de cada uno.", copy: "Copiar enlace", copied: "¡Copiado!", download: "Descargar", dissolved: "Este Círculo fue cerrado." },
  nl: { loading: "De Cirkel opent…", notfound: "Deze Cirkel bestaat niet of is ontbonden.", back: "Terug naar start",
    around: "In de Cirkel", joinTitle: "Word lid van de Cirkel", joinSub: "Kies de naam die jou vertegenwoordigt.", namePh: "Je voornaam", joinBtn: "Meedoen", joining: "…", nameReq: "Een naam, alsjeblieft.", photoNote: "Als je bent ingelogd, is je profielfoto zichtbaar voor de andere deelnemers.", hidePhoto: "Mijn foto hier verbergen",
    empty: "Stilte voor de eerste vonk…", thinking: "L'Élan weeft de band…", placeholder: "Deel je gedachte… (typ @élan om te roepen)",
    share: "Delen", close: "Sluit de Cirkel", closing: "…", sealTitle: "Het Zegel van de Cirkel", sealSaved: "Bewaard in ieders Notitieboek.", copy: "Kopieer link", copied: "Gekopieerd!", download: "Downloaden", dissolved: "Deze Cirkel is gesloten." },
};

export default function CercleRoom() {
  const { i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const lang = (i18n.language || "fr").slice(0, 2);
  const el = L[lang] || L.fr;
  const storeKey = `coccon_cercle_${id}`;

  const [room, setRoom] = useState(undefined);
  const [participant, setParticipant] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storeKey) || "null"); } catch { return null; }
  });
  const [nameInput, setNameInput] = useState("");
  const [hidePhoto, setHidePhoto] = useState(false);
  const [joining, setJoining] = useState(false);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [thinking, setThinking] = useState(false);
  const [active, setActive] = useState(true);
  const [isHost, setIsHost] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [seal, setSeal] = useState(null); // { essence }

  const sinceRef = useRef(null);
  const bottomRef = useRef(null);
  const joined = !!participant;

  useEffect(() => {
    api.get(`/cercle/${id}`)
      .then(({ data }) => {
        setRoom(data); setParticipants(data.participants || []); setActive(data.active);
        setIsHost(!!(participant && participant.isHost));
      })
      .catch(() => setRoom(null));
  }, [id]); // eslint-disable-line

  const poll = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (sinceRef.current) params.set("since", sinceRef.current);
      if (participant?.pid) params.set("pid", participant.pid);
      const { data } = await api.get(`/cercle/${id}/state?${params.toString()}`);
      setParticipants(data.participants || []);
      setThinking(!!data.thinking);
      setActive(data.active);
      if (!data.active && data.essence && !seal) setSeal({ essence: data.essence });
      if (data.messages && data.messages.length) {
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const fresh = data.messages.filter((m) => !seen.has(m.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
        const last = data.messages[data.messages.length - 1];
        if (last?.created_at) sinceRef.current = last.created_at;
        if (data.messages.some((m) => m.role === "user")) setPending([]);
      }
    } catch { /* ignore */ }
  }, [id, participant, seal]);

  useEffect(() => {
    if (!joined) return;
    poll();
    const iv = setInterval(poll, 2500);
    return () => clearInterval(iv);
  }, [joined, poll]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, pending, thinking, sending]);

  const join = async () => {
    const name = nameInput.trim();
    if (!name) { toast.error(el.nameReq); return; }
    if (joining) return;
    setJoining(true);
    try {
      const { data } = await api.post(`/cercle/${id}/join`, { name, hide_photo: hidePhoto });
      const p = { pid: data.participant_id, name: data.name };
      localStorage.setItem(storeKey, JSON.stringify(p));
      setParticipant(p);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || el.notfound);
    } finally {
      setJoining(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    const tmpId = `tmp-${Date.now()}`;
    setPending((p) => [...p, { id: tmpId, role: "user", author: participant.name, color: "", content: text, mine: true }]);
    setSending(true);
    try {
      await api.post(`/cercle/${id}/message`, { participant_id: participant.pid, content: text, lang });
      await poll();
    } catch (e) {
      setPending((p) => p.filter((x) => x.id !== tmpId));
      setInput(text);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "…");
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); } catch { /* ignore */ }
    toast.success(el.copied);
  };

  const closeCircle = async () => {
    if (closing) return;
    setClosing(true);
    try {
      const { data } = await api.post(`/cercle/${id}/close`);
      setSeal({ essence: data.essence });
      setActive(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "…");
    } finally {
      setClosing(false);
    }
  };

  const sealUrl = `${BACKEND_URL}/api/cercle/${id}/seal.png`;
  const shareSeal = async () => {
    try {
      const resp = await fetch(sealUrl);
      const blob = await resp.blob();
      const file = new File([blob], "cercle.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Le Cercle · Cocoon Mind" });
        return;
      }
    } catch { /* fall through */ }
    copyLink();
  };

  if (room === undefined) {
    return (
      <div className="min-h-screen starfield flex items-center justify-center">
        <p className="font-display italic text-2xl text-foreground/50 flex items-center gap-3">
          <Loader2 className="animate-spin" size={22} /> {el.loading}
        </p>
      </div>
    );
  }
  if (room === null) {
    return (
      <div className="min-h-screen starfield flex items-center justify-center px-6">
        <div className="glass rounded-3xl p-12 text-center max-w-md" data-testid="cercle-notfound">
          <Circle className="text-foreground/30 mx-auto mb-6" size={34} />
          <p className="font-display italic text-2xl text-foreground/60 mb-8">{el.notfound}</p>
          <Link to="/" className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold text-sm">{el.back}</Link>
        </div>
      </div>
    );
  }

  const rendered = [...messages, ...pending];

  return (
    <div className="flex flex-col bg-background overflow-hidden starfield" style={{ height: "var(--vvh, 100dvh)" }}>
      <header className="glass z-30 flex-shrink-0">
        <div className="px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/atelier"))} data-testid="cercle-back-btn"
              className="text-foreground/60 hover:text-primary transition-colors flex-shrink-0 -ml-1 p-1">
              <ArrowLeft size={20} />
            </button>
            <Link to="/" className="font-display text-lg sm:text-2xl tracking-tight whitespace-nowrap truncate"><CocoonMark withMind /></Link>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            {room.code && <span className="hidden sm:inline text-xs tracking-[0.25em] text-primary/80 font-semibold" data-testid="cercle-room-code">{room.code}</span>}
            {joined && (
              <button onClick={copyLink} data-testid="cercle-room-share" className="inline-flex items-center gap-1.5 text-xs text-foreground/50 hover:text-primary transition-colors">
                <Link2 size={15} /> <span className="hidden sm:inline">{el.share}</span>
              </button>
            )}
            {joined && isHost && active && (
              <button onClick={closeCircle} disabled={closing} data-testid="cercle-close-btn" className="inline-flex items-center gap-1.5 text-xs text-primary/80 hover:text-primary border border-primary/25 rounded-full px-3 py-1.5 transition-colors">
                {closing ? <Loader2 size={13} className="animate-spin" /> : <DoorOpen size={13} />} <span className="hidden sm:inline">{el.close}</span>
              </button>
            )}
            <LanguageSwitcher className="hidden sm:flex" />
          </div>
        </div>
        {joined && participants.length > 0 && (
          <div className="px-4 sm:px-6 pb-3 flex items-center gap-2 flex-wrap" data-testid="cercle-participants">
            <Users size={14} className="text-primary/70" />
            <span className="text-xs text-foreground/40 uppercase tracking-wider">{el.around} :</span>
            {participants.map((p, i) => (
              <span key={i} className="text-xs pl-1 pr-2.5 py-0.5 rounded-full bg-white/5 flex items-center gap-1.5" style={{ color: p.color }}>
                <span className="relative flex-shrink-0">
                  <MemberAvatar uid={p.uid} avatar={p.avatar} name={p.name} color={p.color} cocon={p.cocon} size={22} />
                  <span className="absolute -bottom-0 -right-0 w-2 h-2 rounded-full z-10" style={{ background: p.online ? p.color : "transparent", border: `1px solid ${p.online ? p.color : "rgba(255,255,255,0.25)"}` }} />
                </span>
                {p.name}{p.is_host ? " ★" : ""}{p.cocon && <CoconBadge />}
              </span>
            ))}
          </div>
        )}
      </header>

      {!joined ? (
        <div className="flex-1 flex items-center justify-center px-6">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-10 frame-pulse w-full max-w-md text-center" data-testid="cercle-join-screen">
            <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-6"><Circle className="text-primary" size={26} /></div>
            <h1 className="font-display text-3xl text-gradient-orange mb-3">{el.joinTitle}</h1>
            {room.question && <p className="font-display italic text-foreground/60 mb-4">« {room.question} »</p>}
            <p className="text-foreground/55 mb-6">{el.joinSub}</p>
            <input
              data-testid="cercle-name-input"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && join()}
              placeholder={el.namePh}
              maxLength={40}
              className="w-full bg-black/30 border border-white/15 rounded-full px-5 py-3 text-center outline-none text-foreground placeholder:text-foreground/30 focus:border-primary/50 transition-colors mb-5"
            />
            <button data-testid="cercle-join-confirm" onClick={join} disabled={joining} className="btn-frost-orange w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-semibold text-base disabled:opacity-50">
              {joining ? <Loader2 size={18} className="animate-spin" /> : <Users size={18} />}
              {joining ? el.joining : el.joinBtn}
            </button>
            <p className="text-[11px] text-foreground/40 leading-relaxed mt-4 flex items-center justify-center gap-1.5" data-testid="cercle-photo-note">
              <ImageIcon size={12} className="flex-shrink-0" /> {el.photoNote}
            </p>
            <button
              type="button"
              data-testid="cercle-hide-photo-toggle"
              aria-pressed={hidePhoto}
              onClick={() => setHidePhoto((v) => !v)}
              className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium border transition-colors ${
                hidePhoto ? "border-primary/50 bg-primary/12 text-primary" : "border-white/12 text-foreground/50 hover:text-foreground/80 hover:border-white/25"
              }`}
            >
              {hidePhoto ? <EyeOff size={13} /> : <Eye size={13} />} {el.hidePhoto}
            </button>
          </motion.div>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto scroll-thin">
            <div className="max-w-3xl mx-auto px-6 py-8">
              {room.question && (
                <div className="text-center mb-8">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-primary/60">Le Cercle</span>
                  <p className="font-display italic text-gradient-orange text-2xl mt-1">« {room.question} »</p>
                </div>
              )}
              <div className="space-y-7">
                {rendered.length === 0 && !thinking && (
                  <p className="font-display italic text-foreground/40 text-xl text-center py-10">{el.empty}</p>
                )}
                {rendered.map((m) => (
                  <motion.div key={m.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid={`cercle-msg-${m.role}`}>
                    {m.role === "assistant" ? (
                      <div className="flex gap-4">
                        <Sparkles className="text-primary mt-1.5 flex-shrink-0 pulse-star" size={20} />
                        <div className="border-l border-primary/20 pl-5 flex-1">
                          <p className="font-display italic text-foreground/85 leading-[1.85] text-lg">{m.content}</p>
                        </div>
                      </div>
                    ) : m.role === "system" ? (
                      <p className="text-center text-xs text-foreground/35 italic">— {m.content} —</p>
                    ) : (
                      <div className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                        <div className={`flex items-start gap-2.5 max-w-xl ${m.mine ? "flex-row-reverse" : "flex-row"}`}>
                          <MemberAvatar uid={m.uid} avatar={m.avatar} name={m.author} color={m.color} cocon={m.cocon} size={30} />
                          <div className={m.mine ? "text-right" : "text-left"}>
                            <div className={`text-xs uppercase tracking-wider mb-1 inline-flex items-center gap-1.5 ${m.mine ? "flex-row-reverse" : ""}`} style={{ color: m.color || "var(--primary)" }}>{m.author}{m.cocon && <CoconBadge />}</div>
                            <p className="font-display italic text-xl text-foreground/80 leading-relaxed">« {m.content} »</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
                <AnimatePresence>
                  {(thinking || sending) && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex gap-4" data-testid="cercle-thinking">
                      <Sparkles className="text-primary mt-1.5 pulse-star" size={20} />
                      <p className="font-display italic text-foreground/40 text-lg">{el.thinking}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
                <div ref={bottomRef} />
              </div>
            </div>
          </div>

          {active ? (
            <div className="flex-shrink-0 border-t border-white/10 glass">
              <div className="w-full px-3 sm:px-6 py-3 flex items-end gap-2 sm:gap-3">
                <textarea
                  data-testid="cercle-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={1}
                  placeholder={el.placeholder}
                  className="flex-1 bg-transparent resize-none outline-none text-foreground placeholder:text-foreground/30 text-lg py-3 max-h-40 scroll-thin"
                />
                <button data-testid="cercle-send-btn" onClick={send} disabled={sending || !input.trim()} className="flex-shrink-0 w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-30">
                  {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-shrink-0 border-t border-white/10 glass py-4 text-center text-sm text-foreground/45">{el.dissolved}</div>
          )}
        </>
      )}

      <AnimatePresence>
        {seal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="cercle-seal-modal">
            <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} className="glass rounded-3xl p-6 sm:p-8 max-w-md w-full text-center relative">
              <button onClick={() => setSeal(null)} data-testid="cercle-seal-close" className="absolute top-4 right-4 text-foreground/40 hover:text-primary transition-colors"><X size={22} /></button>
              <h2 className="font-display text-2xl text-gradient-orange mb-4">{el.sealTitle}</h2>
              <img src={sealUrl} alt="Sceau du Cercle" className="w-full rounded-2xl border border-primary/20 mb-5" data-testid="cercle-seal-img" />
              <p className="text-xs text-foreground/45 mb-5">{el.sealSaved}</p>
              <div className="flex items-center justify-center gap-3">
                <a href={sealUrl} download="cercle-cocoon.png" data-testid="cercle-seal-download" className="inline-flex items-center gap-1.5 text-sm text-foreground/70 hover:text-primary border border-white/10 hover:border-primary/40 rounded-full px-4 py-2 transition-colors">
                  <Download size={14} /> {el.download}
                </a>
                <button onClick={shareSeal} data-testid="cercle-seal-share" className="inline-flex items-center gap-1.5 text-sm text-primary border border-primary/30 hover:border-primary/60 bg-primary/5 rounded-full px-4 py-2 transition-colors">
                  <Share2 size={14} /> {el.share}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
